import { useState, useEffect } from 'react';
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  Clock,
  Award,
  Trophy,
  X,
  ArrowRight
} from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import BeltBadge from '../shared/BeltBadge';

// Tooltip component for calendar icons
const EventTooltip = ({ children, content }) => {
  return (
    <div className="relative group/tooltip">
      {children}
      <div className="absolute hidden group-hover/tooltip:block bg-stone-900 dark:bg-stone-700 text-white text-xs rounded px-2 py-1 -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap z-20 shadow-lg">
        {content}
        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-stone-900 dark:border-t-stone-700" />
      </div>
    </div>
  );
};

// Detailed event card for selected day view
const DayEventDetail = ({ event }) => {
  const getEventStyles = (type) => {
    switch (type) {
      case 'attendance':
        return { bg: 'bg-green-50 dark:bg-green-900/30', border: 'border-green-200 dark:border-green-700', icon: <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" /> };
      case 'promotion':
        return { bg: 'bg-amber-50 dark:bg-amber-900/30', border: 'border-amber-200 dark:border-amber-700', icon: <Award className="h-5 w-5 text-amber-600 dark:text-amber-400" /> };
      case 'tournament':
        return { bg: 'bg-purple-50 dark:bg-purple-900/30', border: 'border-purple-200 dark:border-purple-700', icon: <Trophy className="h-5 w-5 text-purple-600 dark:text-purple-400" /> };
      default:
        return { bg: 'bg-stone-50 dark:bg-stone-800', border: 'border-stone-200 dark:border-stone-600', icon: <CheckCircle className="h-5 w-5 text-stone-400" /> };
    }
  };

  const styles = getEventStyles(event.type);

  if (event.type === 'attendance') {
    const hours = (event.duration_minutes || 60) / 60;
    return (
      <div className={`p-3 rounded-lg border ${styles.bg} ${styles.border}`}>
        <div className="flex items-start gap-3">
          {styles.icon}
          <div className="flex-1">
            <p className="font-medium text-stone-800 dark:text-stone-100">{event.title || 'Class Attendance'}</p>
            <div className="mt-1 text-sm text-stone-600 dark:text-stone-300 space-y-0.5">
              <p className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                Duration: {hours} hour{hours !== 1 ? 's' : ''}
              </p>
              {event.event_type && (
                <p>Type: {event.event_type}</p>
              )}
              {event.notes && (
                <p className="text-stone-500 dark:text-stone-400 italic mt-1">{event.notes}</p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (event.type === 'promotion') {
    return (
      <div className={`p-3 rounded-lg border ${styles.bg} ${styles.border}`}>
        <div className="flex items-start gap-3">
          {styles.icon}
          <div className="flex-1">
            <p className="font-medium text-stone-800 dark:text-stone-100">Belt Promotion</p>
            <div className="mt-2 flex items-center gap-2">
              {event.from_belt && (
                <>
                  <BeltBadge belt={event.from_belt} size="sm" />
                  <ArrowRight className="h-4 w-4 text-stone-400" />
                </>
              )}
              <BeltBadge belt={event.to_belt} size="sm" />
            </div>
            <p className="text-sm text-stone-600 dark:text-stone-300 mt-1">{event.belt_name || event.title}</p>
            {event.notes && (
              <p className="text-sm text-stone-500 dark:text-stone-400 italic mt-1">{event.notes}</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (event.type === 'tournament') {
    return (
      <div className={`p-3 rounded-lg border ${styles.bg} ${styles.border}`}>
        <div className="flex items-start gap-3">
          {styles.icon}
          <div className="flex-1">
            <p className="font-medium text-stone-800 dark:text-stone-100">{event.title || 'Tournament'}</p>
            {event.location && (
              <p className="text-sm text-stone-600 dark:text-stone-300 mt-1">{event.location}</p>
            )}
            {event.results && event.results.length > 0 && (
              <div className="mt-2 space-y-1">
                {event.results.map((result, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm">
                    <span className={`
                      px-2 py-0.5 rounded text-xs font-medium
                      ${result.placement === 1 ? 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300' :
                        result.placement === 2 ? 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200' :
                        result.placement === 3 ? 'bg-orange-100 dark:bg-orange-900/50 text-orange-800 dark:text-orange-300' :
                        'bg-stone-100 dark:bg-stone-700 text-stone-600 dark:text-stone-300'}
                    `}>
                      {result.placement === 1 ? '🥇' : result.placement === 2 ? '🥈' : result.placement === 3 ? '🥉' : `#${result.placement}`}
                    </span>
                    <span className="text-stone-700 dark:text-stone-200">{result.event_name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Default fallback
  return (
    <div className={`p-3 rounded-lg border ${styles.bg} ${styles.border}`}>
      <div className="flex items-center gap-2">
        {styles.icon}
        <span className="font-medium text-stone-800 dark:text-stone-100">{event.title || 'Activity'}</span>
      </div>
    </div>
  );
};

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const getAuthHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('memberToken') || localStorage.getItem('customerToken')}`,
  'Content-Type': 'application/json'
});

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const AttendanceCalendar = ({ student }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarData, setCalendarData] = useState({ events: [] });
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null); // Track clicked day

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  useEffect(() => {
    if (student?.id) {
      fetchCalendarData();
    }
  }, [student?.id, year, month]);

  const fetchCalendarData = async () => {
    setLoading(true);
    try {
      // Use the calendar endpoint which includes attendance, promotions, and tournaments
      const response = await fetch(
        `${API_BASE}/api/customer/students/${student.id}/calendar?year=${year}&month=${month + 1}`,
        { headers: getAuthHeaders() }
      );
      if (response.ok) {
        const data = await response.json();
        setCalendarData(data);
      }
    } catch (error) {
      console.error('Failed to fetch calendar data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper to normalize dates from API (handles both ISO strings and date-only strings)
  const normalizeDate = (dateStr) => {
    if (!dateStr) return null;
    // Remove time portion if present and just get YYYY-MM-DD
    return dateStr.split('T')[0];
  };

  const prevMonth = (e) => {
    e.preventDefault();
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const nextMonth = (e) => {
    e.preventDefault();
    setCurrentDate(new Date(year, month + 1, 1));
  };

  // Build calendar grid
  const firstDayOfMonth = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrevMonth = new Date(year, month, 0).getDate();

  const calendarDays = [];

  // Previous month's trailing days
  for (let i = firstDayOfMonth - 1; i >= 0; i--) {
    calendarDays.push({
      day: daysInPrevMonth - i,
      isCurrentMonth: false,
      date: new Date(year, month - 1, daysInPrevMonth - i)
    });
  }

  // Current month's days
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push({
      day,
      isCurrentMonth: true,
      date: new Date(year, month, day)
    });
  }

  // Next month's leading days to complete the grid
  const remainingDays = 42 - calendarDays.length; // 6 rows * 7 days
  for (let day = 1; day <= remainingDays; day++) {
    calendarDays.push({
      day,
      isCurrentMonth: false,
      date: new Date(year, month + 1, day)
    });
  }

  // Get events for a specific date
  const getEventsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return (calendarData.events || []).filter(e => normalizeDate(e.date) === dateStr);
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Calculate stats from events
  const attendanceEvents = (calendarData.events || []).filter(e => e.type === 'attendance');
  const promotionEvents = (calendarData.events || []).filter(e => e.type === 'promotion');
  const tournamentEvents = (calendarData.events || []).filter(e => e.type === 'tournament');

  const totalClasses = attendanceEvents.length;
  const totalHours = attendanceEvents.reduce((sum, a) => sum + (a.duration_minutes || 60), 0) / 60;

  // Generate tooltip content for an event
  const getTooltipContent = (event) => {
    switch (event.type) {
      case 'attendance': {
        const hours = (event.duration_minutes || 60) / 60;
        return `Class: ${event.title || 'Training'} (${hours}hr)`;
      }
      case 'promotion':
        return `Promoted to ${event.belt_name || 'new belt'}`;
      case 'tournament':
        return event.title || 'Tournament';
      default:
        return event.title || 'Activity';
    }
  };

  // Handle day click
  const handleDayClick = (dayInfo, dayEvents) => {
    if (!dayInfo.isCurrentMonth || dayEvents.length === 0) return;

    const dateStr = dayInfo.date.toISOString().split('T')[0];
    if (selectedDate === dateStr) {
      setSelectedDate(null); // Toggle off if already selected
    } else {
      setSelectedDate(dateStr);
    }
  };

  // Get events for selected date
  const selectedDateEvents = selectedDate
    ? (calendarData.events || []).filter(e => normalizeDate(e.date) === selectedDate)
    : [];

  // Format date for display
  const formatSelectedDate = () => {
    if (!selectedDate) return '';
    const date = new Date(selectedDate + 'T12:00:00'); // Avoid timezone issues
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  };

  if (!student) {
    return null;
  }

  return (
    <Card className="member-card">
      <CardHeader className="pb-2 border-b border-stone-100 dark:border-stone-700 bg-gradient-to-r from-transparent to-amber-50/30 dark:to-amber-900/10">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2 text-stone-800 dark:text-stone-100">
            <Calendar className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            Attendance Calendar
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button type="button" variant="memberSecondary" size="sm" onClick={prevMonth}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm font-medium min-w-[140px] text-center text-stone-700 dark:text-stone-200">
              {MONTHS[month]} {year}
            </span>
            <Button type="button" variant="memberSecondary" size="sm" onClick={nextMonth}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-4">
        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="member-stat-card text-center">
            <p className="text-2xl font-bold text-stone-800 dark:text-stone-100">{totalClasses}</p>
            <p className="text-xs text-stone-500 dark:text-stone-400">Classes This Month</p>
          </div>
          <div className="member-stat-card text-center">
            <p className="text-2xl font-bold text-stone-800 dark:text-stone-100">{totalHours.toFixed(1)}</p>
            <p className="text-xs text-stone-500 dark:text-stone-400">Hours Trained</p>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-8 text-stone-500 dark:text-stone-400">Loading...</div>
        ) : (
          <>
            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1">
              {/* Day headers */}
              {DAYS.map(day => (
                <div
                  key={day}
                  className="text-center text-xs font-medium text-stone-500 dark:text-stone-400 py-2"
                >
                  {day}
                </div>
              ))}

              {/* Calendar days */}
              {calendarDays.map((dayInfo, idx) => {
                const dayEvents = getEventsForDate(dayInfo.date);
                const attendanceEvent = dayEvents.find(e => e.type === 'attendance');
                const promotionEvent = dayEvents.find(e => e.type === 'promotion');
                const tournamentEvent = dayEvents.find(e => e.type === 'tournament');
                const hasAttendance = !!attendanceEvent;
                const hasPromotion = !!promotionEvent;
                const hasTournament = !!tournamentEvent;
                const isToday = dayInfo.date.getTime() === today.getTime();
                const dateStr = dayInfo.date.toISOString().split('T')[0];
                const isSelected = selectedDate === dateStr && dayInfo.isCurrentMonth;
                const isClickable = dayInfo.isCurrentMonth && dayEvents.length > 0;

                return (
                  <div
                    key={idx}
                    onClick={() => handleDayClick(dayInfo, dayEvents)}
                    className={`
                      relative p-2 min-h-[48px] rounded-lg text-center transition-all
                      ${dayInfo.isCurrentMonth ? 'bg-white/60 dark:bg-stone-800/60' : 'bg-stone-50/50 dark:bg-stone-900/50 text-stone-400'}
                      ${isToday ? 'ring-2 ring-amber-500' : ''}
                      ${hasAttendance && dayInfo.isCurrentMonth ? 'bg-green-50 dark:bg-green-900/30' : ''}
                      ${hasPromotion && dayInfo.isCurrentMonth ? 'bg-amber-50 dark:bg-amber-900/30' : ''}
                      ${hasTournament && dayInfo.isCurrentMonth ? 'bg-purple-50 dark:bg-purple-900/30' : ''}
                      ${isSelected ? 'ring-2 ring-[#C9A227] bg-[#9B1B30]/10 dark:bg-[#9B1B30]/20' : ''}
                      ${isClickable ? 'cursor-pointer hover:ring-2 hover:ring-stone-300 dark:hover:ring-stone-500' : ''}
                    `}
                  >
                    <span className={`text-sm ${isToday ? 'font-bold text-amber-600 dark:text-amber-400' : 'text-stone-700 dark:text-stone-200'}`}>
                      {dayInfo.day}
                    </span>
                    {dayInfo.isCurrentMonth && dayEvents.length > 0 && (
                      <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 flex gap-0.5">
                        {hasAttendance && (
                          <EventTooltip content={getTooltipContent(attendanceEvent)}>
                            <CheckCircle className="h-3 w-3 text-green-600 dark:text-green-400" />
                          </EventTooltip>
                        )}
                        {hasPromotion && (
                          <EventTooltip content={getTooltipContent(promotionEvent)}>
                            <Award className="h-3 w-3 text-amber-600 dark:text-amber-400" />
                          </EventTooltip>
                        )}
                        {hasTournament && (
                          <EventTooltip content={getTooltipContent(tournamentEvent)}>
                            <Trophy className="h-3 w-3 text-purple-600 dark:text-purple-400" />
                          </EventTooltip>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Legend */}
            <div className="flex items-center justify-center gap-4 mt-4 pt-4 border-t border-stone-100 dark:border-stone-700 text-sm flex-wrap">
              <div className="flex items-center gap-1">
                <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                <span className="text-stone-600 dark:text-stone-300">Class</span>
              </div>
              <div className="flex items-center gap-1">
                <Award className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                <span className="text-stone-600 dark:text-stone-300">Promotion</span>
              </div>
              <div className="flex items-center gap-1">
                <Trophy className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                <span className="text-stone-600 dark:text-stone-300">Tournament</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 rounded ring-2 ring-amber-500" />
                <span className="text-stone-600 dark:text-stone-300">Today</span>
              </div>
            </div>

            {/* Activity Section - Either selected day detail or recent activity */}
            {(calendarData.events || []).length > 0 && (
              <div className="mt-4 pt-4 border-t border-stone-100 dark:border-stone-700">
                {selectedDate ? (
                  // Selected Day Detail View
                  <>
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm font-medium text-stone-700 dark:text-stone-200">
                        Activity for {formatSelectedDate()}
                      </p>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedDate(null)}
                        className="h-7 px-2 text-xs text-stone-500 dark:text-stone-400 hover:text-stone-700 dark:hover:text-stone-200"
                      >
                        <X className="h-3 w-3 mr-1" />
                        Clear
                      </Button>
                    </div>
                    {selectedDateEvents.length === 0 ? (
                      <p className="text-sm text-stone-500 dark:text-stone-400 text-center py-4">No activity recorded for this day</p>
                    ) : (
                      <div className="space-y-3">
                        {selectedDateEvents.map((event) => (
                          <DayEventDetail key={event.id} event={event} />
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  // Recent Activity (default view)
                  <>
                    <p className="text-sm font-medium text-stone-700 dark:text-stone-200 mb-2">Recent Activity</p>
                    <div className="space-y-2">
                      {(calendarData.events || [])
                        .sort((a, b) => new Date(b.date) - new Date(a.date))
                        .slice(0, 5)
                        .map((event) => {
                          const getEventIcon = (type) => {
                            switch (type) {
                              case 'attendance':
                                return <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />;
                              case 'promotion':
                                return <Award className="h-4 w-4 text-amber-600 dark:text-amber-400" />;
                              case 'tournament':
                                return <Trophy className="h-4 w-4 text-purple-600 dark:text-purple-400" />;
                              default:
                                return <CheckCircle className="h-4 w-4 text-stone-400" />;
                            }
                          };

                          const getEventBg = (type) => {
                            switch (type) {
                              case 'attendance': return 'bg-green-50 dark:bg-green-900/30';
                              case 'promotion': return 'bg-amber-50 dark:bg-amber-900/30';
                              case 'tournament': return 'bg-purple-50 dark:bg-purple-900/30';
                              default: return 'bg-stone-50 dark:bg-stone-800';
                            }
                          };

                          return (
                            <div
                              key={event.id}
                              className={`flex items-center justify-between p-2 rounded text-sm ${getEventBg(event.type)}`}
                            >
                              <div className="flex items-center gap-2">
                                {getEventIcon(event.type)}
                                <span className="text-stone-800 dark:text-stone-100">{event.title || 'Activity'}</span>
                              </div>
                              <div className="flex items-center gap-2 text-stone-500 dark:text-stone-400">
                                <Clock className="h-3 w-3" />
                                <span>{new Date(event.date).toLocaleDateString()}</span>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default AttendanceCalendar;
